import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
  import {
    calculatePolygonCentroid,
    calculateBoundingBoxCenter,
    getLotLabelGap,
    LOT_NUMBER_FONT_SIZE,
    LOT_SIZE_FONT_SIZE,
    LOT_SIZE_DEFAULT_SCALE,
  } from "@/helpers/lotNumber/lotCentroidCalculator";
  import {
    buildQuadraticShapePath,
    convertSvgPathToEditableGeometry,
    getEdgeMidpoint,
    getQuadraticControlPoint,
    getQuadraticShapeBounds,
    type CurvePoint,
  } from "@/helpers/masterplan/curvedLotShape";

  interface SvgElement {
    elementIndex: number;
    type: "rect" | "path" | "polygon" | "circle";
    id?: string;
    boundingBox: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    shapePath?: string;
    statusBarPath?: string;
    polygonPoints?: Array<{ x: number; y: number }>;
    curvePoints?: CurvePoint[];
    rawAttributes: Record<string, string>;
  }

  interface Lot {
    lotId: string;
    lotTitle: string;
    lotStatus: string;
    stageId: string;
    stageTitle: string;
    lotPrice?: number;
    lotSize?: number;
  }

  interface Assignment {
    elementIndex: number;
    type: string;
    lotId: string;
    boundingBox: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    shapePath?: string;
    statusBarPath?: string;
    polygonPoints?: Array<{ x: number; y: number }>;
    curvePoints?: CurvePoint[];
    lotNumberMetadata?: {
      offsetX: number;
      offsetY: number;
      scale: number;
      rotation: number;
      displayed: boolean;
    };
    lotSizeMetadata?: {
      offsetX: number;
      offsetY: number;
      scale: number;
      rotation: number;
      displayed: boolean;
    };
  }

  interface LotNumberMetadata {
    lotId: string;
    offsetX: number;
    offsetY: number;
    scale: number;
    rotation: number;
    displayed: boolean;
  }

  type LotSizeMetadata = LotNumberMetadata;

  const CORNER_HANDLE_RADIUS = 3.75;
  const MIDPOINT_HANDLE_RADIUS = 3;

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LotMatcher',
  props: {
    svgFile: {},
    elements: {},
    availableLots: {},
    initialAssignments: {}
  },
  emits: ["complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emit = __emit;

  const localElements = ref<SvgElement[]>([...props.elements]);

  const svgContainer = ref<HTMLDivElement | null>(null);
  const svgElement = ref<SVGElement | null>(null);
  const svgOverlay = ref<SVGSVGElement | null>(null);
  const svgContent = ref("");
  const overlayViewBox = ref("0 0 1000 1000");
  const zoomLevel = ref(1);
  const panX = ref(0);
  const panY = ref(0);
  const isDragging = ref(false);
  const dragStart = ref({ x: 0, y: 0 });
  const selectedElement = ref<SvgElement | null>(null);
  const editingElement = ref<SvgElement | null>(null);
  const isDrawing = ref(false);
  const drawingPoints = ref<Array<{ x: number; y: number }>>([]);
  const draggedCorner = ref<string | null>(null);
  const draggedPolygonPointIndex = ref<number | null>(null);
  const draggedCurveEdgeIndex = ref<number | null>(null);
  const curveDragStart = ref({ x: 0, y: 0 });
  const curveHandleWasDragged = ref(false);
  const selectedLotId = ref("");
  const lotSearch = ref("");
  const assignments = ref<Assignment[]>(
    props.initialAssignments ? [...props.initialAssignments] : []
  );

  const lotNumberAdjustments = ref({
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    rotation: 0,
    displayed: true,
  });

  const lotSizeAdjustments = ref({
    offsetX: 2,
    offsetY: 2,
    scale: LOT_SIZE_DEFAULT_SCALE,
    rotation: 0,
    displayed: true,
  });

  const isAssigned = (elementIndex: number): boolean => {
    return assignments.value.some((a) => a.elementIndex === elementIndex);
  };

  const svgStyle = computed(() => ({
    transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoomLevel.value})`,
    transformOrigin: "0 0",
    cursor: isDrawing.value
      ? "crosshair"
      : isDragging.value
      ? "grabbing"
      : "grab",
  }));

  const assignedCount = computed(() => assignments.value.length);

  const unassignedCount = computed(
    () => localElements.value.length - assignments.value.length
  );

  const filteredLots = computed(() => {
    if (!lotSearch.value) return props.availableLots;

    const search = lotSearch.value.toLowerCase();

    return props.availableLots.filter(
      (lot) =>
        lot.lotTitle?.toLowerCase().includes(search) ||
        lot.lotStatus?.toLowerCase().includes(search) ||
        lot.stageTitle?.toLowerCase().includes(search)
    );
  });

  const getLotName = (lotId: string): string => {
    const lot = props.availableLots.find((l) => l.lotId === lotId);

    return lot ? lot.lotTitle : "Unknown";
  };

  const calculateLotCenter = (
    element: SvgElement
  ): { x: number; y: number } | null => {
    if (element.polygonPoints && element.polygonPoints.length > 0) {
      return calculatePolygonCentroid(element.polygonPoints);
    } else if (element.boundingBox) {
      return calculateBoundingBoxCenter(element.boundingBox);
    }

    return null;
  };

  const getLotNumberForElement = (elementIndex: number): string | null => {
    const assignment = assignments.value.find(
      (a) => a.elementIndex === elementIndex
    );

    if (!assignment) return null;

    const lot = props.availableLots.find((l) => l.lotId === assignment.lotId);

    return lot ? lot.lotTitle : null;
  };

  const getLotSizeForElement = (elementIndex: number): string | null => {
    const assignment = assignments.value.find(
      (a) => a.elementIndex === elementIndex
    );

    if (!assignment) return null;

    const lot = props.availableLots.find((l) => l.lotId === assignment.lotId);

    if (lot?.lotSize === null || lot?.lotSize === undefined) return null;

    return `${lot.lotSize}m²`;
  };

  const shouldShowLotSize = (elementIndex: number): boolean => {
    const assignment = assignments.value.find(
      (a) => a.elementIndex === elementIndex
    );

    return assignment?.lotSizeMetadata?.displayed !== false;
  };

  const getLotNumberX = (element: SvgElement): number => {
    const center = calculateLotCenter(element);

    if (!center) return 0;

    const assignment = assignments.value.find(
      (a) => a.elementIndex === element.elementIndex
    );
    const metadata = assignment?.lotNumberMetadata;

    return center.x + (metadata?.offsetX || 0);
  };

  const getLotNumberY = (element: SvgElement): number => {
    const center = calculateLotCenter(element);

    if (!center) return 0;

    const assignment = assignments.value.find(
      (a) => a.elementIndex === element.elementIndex
    );
    const metadata = assignment?.lotNumberMetadata;

    return center.y + (metadata?.offsetY || 0);
  };

  const getLotNumberTransform = (element: SvgElement): string => {
    const center = calculateLotCenter(element);

    if (!center) return "";

    const assignment = assignments.value.find(
      (a) => a.elementIndex === element.elementIndex
    );
    const metadata = assignment?.lotNumberMetadata;

    if (!metadata || (metadata.scale === 1 && metadata.rotation === 0))
      return "";

    const x = center.x + (metadata.offsetX || 0);
    const y = center.y + (metadata.offsetY || 0);

    return `translate(${x}, ${y}) scale(${metadata.scale}) rotate(${
      metadata.rotation
    }) translate(${-x}, ${-y})`;
  };

  const getLotSizeX = (element: SvgElement): number => {
    const center = calculateLotCenter(element);

    if (!center) return 0;

    const assignment = assignments.value.find(
      (a) => a.elementIndex === element.elementIndex
    );

    return (
      center.x +
      (assignment?.lotNumberMetadata?.offsetX || 0) +
      (assignment?.lotSizeMetadata?.offsetX ?? 2)
    );
  };

  const getLotSizeY = (element: SvgElement): number => {
    const center = calculateLotCenter(element);

    if (!center) return 0;

    const assignment = assignments.value.find(
      (a) => a.elementIndex === element.elementIndex
    );
    const numberScale = assignment?.lotNumberMetadata?.scale ?? 1;
    const sizeScale =
      assignment?.lotSizeMetadata?.scale ?? LOT_SIZE_DEFAULT_SCALE;
    const labelGap = getLotLabelGap(numberScale, sizeScale);

    return (
      center.y +
      (assignment?.lotNumberMetadata?.offsetY || 0) +
      labelGap +
      (assignment?.lotSizeMetadata?.offsetY ?? 2)
    );
  };

  const getLotSizeTransform = (element: SvgElement): string => {
    const assignment = assignments.value.find(
      (a) => a.elementIndex === element.elementIndex
    );
    const metadata = assignment?.lotSizeMetadata;
    const scale = metadata?.scale ?? LOT_SIZE_DEFAULT_SCALE;
    const rotation = metadata?.rotation ?? 0;

    if (scale === 1 && rotation === 0) return "";

    const x = getLotSizeX(element);
    const y = getLotSizeY(element);

    return `translate(${x}, ${y}) scale(${scale}) rotate(${rotation}) translate(${-x}, ${-y})`;
  };

  const handleEditLotNumber = () => {
    // kept for future use
  };

  // Watch for real-time updates to lot number adjustments
  watch(
    lotNumberAdjustments,
    (newVal) => {
      const selected = selectedElement.value;

      if (!selected) return;

      const assignment = assignments.value.find(
        (a) => a.elementIndex === selected.elementIndex
      );

      if (!assignment) return;

      assignment.lotNumberMetadata = {
        offsetX: newVal.offsetX,
        offsetY: newVal.offsetY,
        scale: newVal.scale,
        rotation: newVal.rotation,
        displayed: newVal.displayed,
      };
    },
    { deep: true }
  );

  watch(
    lotSizeAdjustments,
    (newVal) => {
      const selected = selectedElement.value;

      if (!selected) return;

      const assignment = assignments.value.find(
        (a) => a.elementIndex === selected.elementIndex
      );

      if (!assignment) return;

      assignment.lotSizeMetadata = { ...newVal };
    },
    { deep: true }
  );

  onMounted(async () => {
    const reader = new FileReader();

    reader.onload = (e) => {
      svgContent.value = e.target?.result as string;

      setTimeout(() => {
        extractViewBox();
      }, 100);
    };
    reader.readAsText(props.svgFile);

    window.addEventListener("keydown", handleKeyPress);
  });

  onUnmounted(() => {
    window.removeEventListener("keydown", handleKeyPress);
    document.removeEventListener("mousemove", handleCurveDrag);
    document.removeEventListener("mouseup", handleCurveDragEnd);
  });

  const handleKeyPress = (event: KeyboardEvent) => {
    if (!(event.target instanceof HTMLElement)) return;

    const target = event.target;

    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT" ||
      target.isContentEditable
    )
      return;

    if (isDrawing.value) {
      if (event.key === "Escape") {
        event.preventDefault();
        handleCancelDrawing();
      } else if (event.key === "Enter" && drawingPoints.value.length >= 3) {
        event.preventDefault();
        handleFinishDrawing();
      } else if (
        (event.key === "Delete" || event.key === "Backspace") &&
        drawingPoints.value.length > 0
      ) {
        event.preventDefault();
        handleUndoDrawingPoint();
      }

      return;
    }

    if (
      (event.key === "Delete" || event.key === "Backspace") &&
      selectedElement.value &&
      !editingElement.value
    ) {
      event.preventDefault();
      handleDeleteElement();
      return;
    }
  };

  const extractViewBox = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent.value, "image/svg+xml");
    const svg = doc.querySelector("svg");

    if (svg) {
      const viewBox = svg.getAttribute("viewBox");

      if (viewBox) {
        overlayViewBox.value = viewBox;
      } else {
        const width = svg.getAttribute("width") || "1000";
        const height = svg.getAttribute("height") || "1000";

        overlayViewBox.value = `0 0 ${width} ${height}`;
      }
    }
  };

  const handleElementClick = (element: SvgElement) => {
    if (isDrawing.value) return;

    selectedElement.value = element;
    selectedLotId.value = "";
    lotSearch.value = "";

    const existing = assignments.value.find(
      (a) => a.elementIndex === element.elementIndex
    );

    if (existing) {
      selectedLotId.value = existing.lotId;
      // Load current metadata into adjustment panel
      const meta = existing.lotNumberMetadata;

      lotNumberAdjustments.value = {
        offsetX: meta?.offsetX ?? 0,
        offsetY: meta?.offsetY ?? 0,
        scale: meta?.scale ?? 1,
        rotation: meta?.rotation ?? 0,
        displayed: meta?.displayed !== false,
      };
      const sizeMeta = existing.lotSizeMetadata;

      lotSizeAdjustments.value = {
        offsetX: sizeMeta?.offsetX ?? 2,
        offsetY: sizeMeta?.offsetY ?? 2,
        scale: sizeMeta?.scale ?? LOT_SIZE_DEFAULT_SCALE,
        rotation: sizeMeta?.rotation ?? 0,
        displayed: sizeMeta?.displayed !== false,
      };
    }
  };

  const handleSelectAssignmentFromList = (assignment: Assignment) => {
    const element = localElements.value.find(
      (el) => el.elementIndex === assignment.elementIndex
    );

    if (element) handleElementClick(element);
  };

  const handleSelectLot = (lotId: string) => {
    selectedLotId.value = lotId;
  };

  const handleAssign = () => {
    if (!selectedElement.value || !selectedLotId.value) return;

    const elementIndex = selectedElement.value.elementIndex;
    const existingAssignment = assignments.value.find(
      (assignment) => assignment.elementIndex === elementIndex
    );
    const previousAssignment = assignments.value[assignments.value.length - 1];
    const lotNumberMetadata = existingAssignment
      ? { ...lotNumberAdjustments.value }
      : {
          offsetX: 0,
          offsetY: 0,
          scale: previousAssignment?.lotNumberMetadata?.scale ?? 1,
          rotation: 0,
          displayed: true,
        };
    const lotSizeMetadata = existingAssignment
      ? { ...lotSizeAdjustments.value }
      : {
          offsetX: 2,
          offsetY: 2,
          scale:
            previousAssignment?.lotSizeMetadata?.scale ??
            LOT_SIZE_DEFAULT_SCALE,
          rotation: 0,
          displayed: true,
        };

    assignments.value = assignments.value.filter(
      (a) => a.elementIndex !== elementIndex
    );

    assignments.value.push({
      elementIndex: selectedElement.value.elementIndex,
      type: selectedElement.value.type,
      lotId: selectedLotId.value,
      boundingBox: selectedElement.value.boundingBox,
      shapePath: selectedElement.value.shapePath,
      statusBarPath: selectedElement.value.statusBarPath,
      polygonPoints: selectedElement.value.polygonPoints,
      curvePoints: selectedElement.value.curvePoints,
      lotNumberMetadata,
      lotSizeMetadata,
    });

    selectedElement.value = null;
    selectedLotId.value = "";
  };

  const handleSkipElement = () => {
    selectedElement.value = null;
    selectedLotId.value = "";
  };

  const handleStartEdit = () => {
    if (!selectedElement.value) return;

    editingElement.value = JSON.parse(JSON.stringify(selectedElement.value));

    if (editingElement.value?.shapePath) {
      if (
        editingElement.value.polygonPoints &&
        editingElement.value.curvePoints?.length
      ) {
        updateEditedShapeGeometry();
        return;
      }

      const geometry = convertSvgPathToEditableGeometry(
        editingElement.value.shapePath,
        CORNER_HANDLE_RADIUS * 2
      );

      if (geometry) {
        editingElement.value.polygonPoints = geometry.points;
        editingElement.value.curvePoints = geometry.curvePoints;
      } else if (editingElement.value.polygonPoints) {
        editingElement.value.polygonPoints = normalizeEditablePolygonPoints(
          editingElement.value.polygonPoints
        );
        editingElement.value.curvePoints = [];
      }

      updateEditedShapeGeometry();
    }
  };

  const handleSaveEdit = () => {
    if (!editingElement.value) return;

    const elementIndex = editingElement.value.elementIndex;

    const index = localElements.value.findIndex(
      (el) => el.elementIndex === elementIndex
    );

    if (index !== -1) {
      localElements.value[index].boundingBox = {
        ...editingElement.value.boundingBox,
      };

      if (editingElement.value.polygonPoints) {
        localElements.value[index].polygonPoints = [
          ...editingElement.value.polygonPoints,
        ];
        localElements.value[index].curvePoints = [
          ...(editingElement.value.curvePoints ?? []),
        ];
        localElements.value[index].shapePath = editingElement.value.shapePath;
        localElements.value[index].statusBarPath = undefined;
      }
    }

    const assignmentIndex = assignments.value.findIndex(
      (a) => a.elementIndex === elementIndex
    );

    if (assignmentIndex !== -1) {
      assignments.value[assignmentIndex].boundingBox = {
        ...editingElement.value.boundingBox,
      };

      if (editingElement.value.polygonPoints) {
        assignments.value[assignmentIndex].polygonPoints = [
          ...editingElement.value.polygonPoints,
        ];
        assignments.value[assignmentIndex].curvePoints = [
          ...(editingElement.value.curvePoints ?? []),
        ];
        assignments.value[assignmentIndex].shapePath =
          editingElement.value.shapePath;
        assignments.value[assignmentIndex].statusBarPath = undefined;
      }
    }

    selectedElement.value = index === -1 ? null : localElements.value[index];
    editingElement.value = null;
  };

  const handleCancelEdit = () => {
    editingElement.value = null;
  };

  const handleDeleteElement = () => {
    if (!selectedElement.value) return;

    if (!confirm("Are you sure you want to delete this element?")) return;

    const elementIndex = selectedElement.value.elementIndex;

    const index = localElements.value.findIndex(
      (el) => el.elementIndex === elementIndex
    );

    if (index !== -1) {
      localElements.value.splice(index, 1);
    }

    assignments.value = assignments.value.filter(
      (a) => a.elementIndex !== elementIndex
    );

    selectedElement.value = null;
    editingElement.value = null;
  };

  const handleCornerDragStart = (event: MouseEvent, corner: string) => {
    event.preventDefault();
    draggedCorner.value = corner;

    document.addEventListener("mousemove", handleCornerDrag);
    document.addEventListener("mouseup", handleCornerDragEnd);
  };

  const handlePolygonCornerDragStart = (
    event: MouseEvent,
    pointIndex: number
  ) => {
    event.preventDefault();
    draggedPolygonPointIndex.value = pointIndex;

    document.addEventListener("mousemove", handlePolygonCornerDrag);
    document.addEventListener("mouseup", handlePolygonCornerDragEnd);
  };

  const handleCornerDrag = (event: MouseEvent) => {
    if (!draggedCorner.value || !editingElement.value || !svgOverlay.value)
      return;

    const svg = svgOverlay.value;
    const pt = svg.createSVGPoint();

    pt.x = event.clientX;
    pt.y = event.clientY;

    const ctm = svg.getScreenCTM();

    if (!ctm) return;

    const svgP = pt.matrixTransform(ctm.inverse());

    const box = editingElement.value.boundingBox;

    switch (draggedCorner.value) {
      case "top-left":
        box.width += box.x - svgP.x;
        box.height += box.y - svgP.y;
        box.x = svgP.x;
        box.y = svgP.y;
        break;
      case "top-right":
        box.width = svgP.x - box.x;
        box.height += box.y - svgP.y;
        box.y = svgP.y;
        break;
      case "bottom-right":
        box.width = svgP.x - box.x;
        box.height = svgP.y - box.y;
        break;
      case "bottom-left":
        box.width += box.x - svgP.x;
        box.x = svgP.x;
        box.height = svgP.y - box.y;
        break;
    }

    if (box.width < 10) box.width = 10;

    if (box.height < 10) box.height = 10;
  };

  const handleCornerDragEnd = () => {
    draggedCorner.value = null;
    document.removeEventListener("mousemove", handleCornerDrag);
    document.removeEventListener("mouseup", handleCornerDragEnd);
  };

  const handlePolygonCornerDrag = (event: MouseEvent) => {
    if (
      draggedPolygonPointIndex.value === null ||
      !editingElement.value?.polygonPoints ||
      !svgOverlay.value
    )
      return;

    const svg = svgOverlay.value;
    const pt = svg.createSVGPoint();

    pt.x = event.clientX;
    pt.y = event.clientY;

    const ctm = svg.getScreenCTM();

    if (!ctm) return;

    const svgP = pt.matrixTransform(ctm.inverse());

    const previousPoint =
      editingElement.value.polygonPoints[draggedPolygonPointIndex.value];
    const delta = {
      x: svgP.x - previousPoint.x,
      y: svgP.y - previousPoint.y,
    };
    const pointCount = editingElement.value.polygonPoints.length;
    const previousEdgeIndex =
      (draggedPolygonPointIndex.value - 1 + pointCount) % pointCount;

    editingElement.value.curvePoints?.forEach((curve) => {
      if (
        curve.edgeIndex === draggedPolygonPointIndex.value ||
        curve.edgeIndex === previousEdgeIndex
      ) {
        curve.x += delta.x / 2;
        curve.y += delta.y / 2;
      }
    });

    editingElement.value.polygonPoints[draggedPolygonPointIndex.value] = {
      x: svgP.x,
      y: svgP.y,
    };

    updateEditedShapeGeometry();
  };

  const handlePolygonCornerDragEnd = () => {
    draggedPolygonPointIndex.value = null;
    document.removeEventListener("mousemove", handlePolygonCornerDrag);
    document.removeEventListener("mouseup", handlePolygonCornerDragEnd);
  };

  const handleCurveDragStart = (event: MouseEvent, edgeIndex: number) => {
    event.preventDefault();
    draggedCurveEdgeIndex.value = edgeIndex;
    curveDragStart.value = { x: event.clientX, y: event.clientY };
    curveHandleWasDragged.value = false;
    document.addEventListener("mousemove", handleCurveDrag);
    document.addEventListener("mouseup", handleCurveDragEnd);
  };

  const getDistanceFromEdge = (
    point: { x: number; y: number },
    start: { x: number; y: number },
    end: { x: number; y: number }
  ): number => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) return getPointDistance(point, start);

    const position = Math.max(
      0,
      Math.min(
        1,
        ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared
      )
    );

    return getPointDistance(point, {
      x: start.x + position * dx,
      y: start.y + position * dy,
    });
  };

  const handleCurveDrag = (event: MouseEvent) => {
    if (
      draggedCurveEdgeIndex.value === null ||
      !editingElement.value?.polygonPoints
    )
      return;

    if (
      Math.hypot(
        event.clientX - curveDragStart.value.x,
        event.clientY - curveDragStart.value.y
      ) >= 3
    ) {
      curveHandleWasDragged.value = true;
    }

    if (!curveHandleWasDragged.value) return;

    const point = getSvgPoint(event);

    if (!point) return;

    const edgeIndex = draggedCurveEdgeIndex.value;
    const points = editingElement.value.polygonPoints;
    const start = points[edgeIndex];
    const end = points[(edgeIndex + 1) % points.length];
    const curvePoints = editingElement.value.curvePoints ?? [];
    const existingIndex = curvePoints.findIndex(
      (curve) => curve.edgeIndex === edgeIndex
    );

    if (getDistanceFromEdge(point, start, end) <= CORNER_HANDLE_RADIUS) {
      if (existingIndex !== -1) curvePoints.splice(existingIndex, 1);
    } else {
      const curvePoint = { edgeIndex, x: point.x, y: point.y };

      if (existingIndex === -1) curvePoints.push(curvePoint);
      else curvePoints[existingIndex] = curvePoint;
    }

    editingElement.value.curvePoints = curvePoints;
    updateEditedShapeGeometry();
  };

  const handleCurveDragEnd = () => {
    draggedCurveEdgeIndex.value = null;
    document.removeEventListener("mousemove", handleCurveDrag);
    document.removeEventListener("mouseup", handleCurveDragEnd);
  };

  const updateEditedShapeGeometry = () => {
    if (
      !editingElement.value?.polygonPoints ||
      editingElement.value.polygonPoints.length === 0
    )
      return;

    editingElement.value.shapePath = buildQuadraticShapePath(
      editingElement.value.polygonPoints,
      editingElement.value.curvePoints
    );
    const bounds = getQuadraticShapeBounds(
      editingElement.value.polygonPoints,
      editingElement.value.curvePoints
    );

    if (bounds) editingElement.value.boundingBox = bounds;
  };

  const handleUnassign = (elementIndex: number) => {
    assignments.value = assignments.value.filter(
      (a) => a.elementIndex !== elementIndex
    );
  };

  const applyZoom = (newZoom: number, anchorX: number, anchorY: number) => {
    const ratio = newZoom / zoomLevel.value;

    panX.value = anchorX - (anchorX - panX.value) * ratio;
    panY.value = anchorY - (anchorY - panY.value) * ratio;
    zoomLevel.value = newZoom;
  };

  const handleZoomIn = () => {
    const container = svgContainer.value;
    const cx = container ? container.clientWidth / 2 : 0;
    const cy = container ? container.clientHeight / 2 : 0;

    applyZoom(Math.min(zoomLevel.value * 1.2, 20), cx, cy);
  };

  const handleZoomOut = () => {
    const container = svgContainer.value;
    const cx = container ? container.clientWidth / 2 : 0;
    const cy = container ? container.clientHeight / 2 : 0;

    applyZoom(Math.max(zoomLevel.value / 1.2, 0.1), cx, cy);
  };

  const handleResetView = () => {
    zoomLevel.value = 1;
    panX.value = 0;
    panY.value = 0;
  };

  const handleWheel = (e: WheelEvent) => {
    const container = svgContainer.value;

    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;

    applyZoom(
      Math.max(0.1, Math.min(20, zoomLevel.value * delta)),
      mouseX,
      mouseY
    );
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (isDrawing.value) return;

    const target = e.target as HTMLElement;

    if (e.button === 0 && !target?.classList?.contains("clickable-element")) {
      isDragging.value = true;
      dragStart.value = {
        x: e.clientX - panX.value,
        y: e.clientY - panY.value,
      };
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging.value) {
      panX.value = e.clientX - dragStart.value.x;
      panY.value = e.clientY - dragStart.value.y;
    }
  };

  const handleMouseUp = () => {
    isDragging.value = false;
  };

  const getSvgPoint = (event: MouseEvent): { x: number; y: number } | null => {
    if (!svgOverlay.value) return null;

    const point = svgOverlay.value.createSVGPoint();

    point.x = event.clientX;
    point.y = event.clientY;

    const ctm = svgOverlay.value.getScreenCTM();

    if (!ctm) return null;

    const svgPoint = point.matrixTransform(ctm.inverse());

    return { x: svgPoint.x, y: svgPoint.y };
  };

  const handleStartDrawing = () => {
    selectedElement.value = null;
    selectedLotId.value = "";
    editingElement.value = null;
    drawingPoints.value = [];
    isDragging.value = false;
    isDrawing.value = true;
  };

  const handleDrawingPoint = (event: MouseEvent) => {
    if (!isDrawing.value) return;

    const point = getSvgPoint(event);

    if (point) drawingPoints.value.push(point);
  };

  const handleUndoDrawingPoint = () => {
    drawingPoints.value.pop();
  };

  const handleRemoveDrawingPoint = (pointIndex: number) => {
    drawingPoints.value.splice(pointIndex, 1);
  };

  const handleCancelDrawing = () => {
    drawingPoints.value = [];
    isDrawing.value = false;
  };

  const handleFinishDrawing = () => {
    if (drawingPoints.value.length < 3) return;

    const points = drawingPoints.value.map((point) => ({ ...point }));
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    if (maxX === minX || maxY === minY) return;

    const nextElementIndex =
      localElements.value.reduce(
        (highest, element) => Math.max(highest, element.elementIndex),
        -1
      ) + 1;
    const element: SvgElement = {
      elementIndex: nextElementIndex,
      type: "polygon",
      boundingBox: {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
      },
      polygonPoints: points,
      curvePoints: [],
      rawAttributes: { "data-manually-created": "true" },
    };

    localElements.value.push(element);
    drawingPoints.value = [];
    isDrawing.value = false;
    handleElementClick(element);
    handleStartEdit();
  };

  const getDrawingPoints = (): string =>
    drawingPoints.value.map((point) => `${point.x},${point.y}`).join(" ");

  const handleConvertToPolygon = () => {
    if (!editingElement.value) return;

    const box = editingElement.value.boundingBox;

    editingElement.value.polygonPoints = [
      { x: box.x, y: box.y },
      { x: box.x + box.width, y: box.y },
      { x: box.x + box.width, y: box.y + box.height },
      { x: box.x, y: box.y + box.height },
    ];
    editingElement.value.curvePoints = [];
    updateEditedShapeGeometry();
  };

  const getPointDistance = (
    first: { x: number; y: number },
    second: { x: number; y: number }
  ): number => Math.hypot(first.x - second.x, first.y - second.y);

  const normalizeEditablePolygonPoints = (
    points: Array<{ x: number; y: number }>
  ): Array<{ x: number; y: number }> => {
    const minimumDistance = CORNER_HANDLE_RADIUS * 2;
    const normalized: Array<{ x: number; y: number }> = [];

    points.forEach((point) => {
      const previous = normalized[normalized.length - 1];

      if (!previous || getPointDistance(previous, point) >= minimumDistance) {
        normalized.push({ ...point });
      }
    });

    while (
      normalized.length > 3 &&
      getPointDistance(normalized[0], normalized[normalized.length - 1]) <
        minimumDistance
    ) {
      normalized.pop();
    }

    return normalized.length >= 3
      ? normalized
      : points.slice(0, 3).map((point) => ({ ...point }));
  };

  const getPolygonMidpoints = (): Array<{
    x: number;
    y: number;
    edgeIndex: number;
    curved: boolean;
  }> => {
    if (!editingElement.value?.polygonPoints) return [];

    const points = editingElement.value.polygonPoints;
    const minimumEdgeLength =
      (CORNER_HANDLE_RADIUS + MIDPOINT_HANDLE_RADIUS) * 2;

    return points.flatMap((point, edgeIndex) => {
      const next = points[(edgeIndex + 1) % points.length];
      const curved = Boolean(
        editingElement.value?.curvePoints?.some(
          (curve) => curve.edgeIndex === edgeIndex
        )
      );

      if (!curved && getPointDistance(point, next) < minimumEdgeLength)
        return [];

      const edgeMidpoint = getEdgeMidpoint(
        points,
        editingElement.value?.curvePoints ?? [],
        edgeIndex
      );

      return [
        {
          ...edgeMidpoint,
          edgeIndex,
          curved,
        },
      ];
    });
  };

  const handleMidpointClick = (edgeIndex: number) => {
    if (curveHandleWasDragged.value) {
      curveHandleWasDragged.value = false;
      return;
    }

    handleAddPolygonPoint(edgeIndex);
  };

  const handleAddPolygonPoint = (edgeIndex: number) => {
    if (!editingElement.value?.polygonPoints) return;

    const points = editingElement.value.polygonPoints;
    const originalPointCount = points.length;
    const nextIndex = (edgeIndex + 1) % originalPointCount;
    const start = points[edgeIndex];
    const end = points[nextIndex];
    const curves = editingElement.value.curvePoints ?? [];
    const existingCurve = curves.find((curve) => curve.edgeIndex === edgeIndex);
    const insertIndex =
      edgeIndex === originalPointCount - 1 ? points.length : nextIndex;
    const newPoint = getEdgeMidpoint(points, curves, edgeIndex);

    const shiftedCurves = curves
      .filter((curve) => curve.edgeIndex !== edgeIndex)
      .map((curve) => ({
        ...curve,
        edgeIndex:
          curve.edgeIndex > edgeIndex ? curve.edgeIndex + 1 : curve.edgeIndex,
      }));

    if (existingCurve) {
      const control = getQuadraticControlPoint(start, existingCurve, end);
      const pointOnCurve = (t: number) => {
        const inverse = 1 - t;

        return {
          x:
            inverse ** 2 * start.x +
            2 * inverse * t * control.x +
            t ** 2 * end.x,
          y:
            inverse ** 2 * start.y +
            2 * inverse * t * control.y +
            t ** 2 * end.y,
        };
      };

      shiftedCurves.push(
        { edgeIndex, ...pointOnCurve(0.25) },
        { edgeIndex: edgeIndex + 1, ...pointOnCurve(0.75) }
      );
    }

    points.splice(insertIndex, 0, newPoint);
    editingElement.value.curvePoints = shiftedCurves;
    updateEditedShapeGeometry();
  };

  const handleRemovePolygonPoint = (pointIndex: number) => {
    if (!editingElement.value?.polygonPoints) return;

    if (editingElement.value.polygonPoints.length <= 3) return;

    const pointCount = editingElement.value.polygonPoints.length;
    const previousEdgeIndex = (pointIndex - 1 + pointCount) % pointCount;

    editingElement.value.curvePoints = (editingElement.value.curvePoints ?? [])
      .filter(
        (curve) =>
          curve.edgeIndex !== previousEdgeIndex &&
          curve.edgeIndex !== pointIndex
      )
      .map((curve) => ({
        ...curve,
        edgeIndex:
          curve.edgeIndex > pointIndex ? curve.edgeIndex - 1 : curve.edgeIndex,
      }));
    editingElement.value.polygonPoints.splice(pointIndex, 1);
    updateEditedShapeGeometry();
  };

  const getElementShapePath = (element: SvgElement): string | undefined => {
    if (editingElement.value?.elementIndex === element.elementIndex) {
      return editingElement.value.shapePath;
    }

    return element.shapePath;
  };

  const getPolygonPoints = (element: SvgElement): string => {
    const isEditing =
      editingElement.value?.elementIndex === element.elementIndex;
    const pointsArray =
      isEditing && editingElement.value?.polygonPoints
        ? editingElement.value.polygonPoints
        : element.polygonPoints;

    if (!pointsArray || pointsArray.length === 0) {
      return "";
    }

    return pointsArray.map((p) => `${p.x},${p.y}`).join(" ");
  };

  const handleComplete = () => {
    const unassignedElements = localElements.value.filter(
      (el) => !assignments.value.some((a) => a.elementIndex === el.elementIndex)
    );

    if (unassignedElements.length > 0) {
      const confirmed = window.confirm(
        `${unassignedElements.length} element(s) are not assigned to any lot and will be permanently removed from the masterplan.\n\nDo you want to continue?`
      );

      if (!confirmed) return;
    }

    const assignedElements = localElements.value.filter((el) =>
      assignments.value.some((a) => a.elementIndex === el.elementIndex)
    );

    // Build lot number metadata array
    const lotNumberMetadata: LotNumberMetadata[] = assignments.value
      .filter((a) => a.lotNumberMetadata)
      .map((a) => ({
        lotId: a.lotId,
        offsetX: a.lotNumberMetadata?.offsetX || 0,
        offsetY: a.lotNumberMetadata?.offsetY || 0,
        scale: a.lotNumberMetadata?.scale || 1,
        rotation: a.lotNumberMetadata?.rotation || 0,
        displayed: a.lotNumberMetadata?.displayed !== false,
      }));

    const lotSizeMetadata: LotSizeMetadata[] = assignments.value.map((a) => ({
      lotId: a.lotId,
      offsetX: a.lotSizeMetadata?.offsetX ?? 2,
      offsetY: a.lotSizeMetadata?.offsetY ?? 2,
      scale: a.lotSizeMetadata?.scale ?? LOT_SIZE_DEFAULT_SCALE,
      rotation: a.lotSizeMetadata?.rotation || 0,
      displayed: a.lotSizeMetadata?.displayed !== false,
    }));

    emit("complete", {
      assignments: assignments.value,
      elements: assignedElements,
      lotNumberMetadata:
        lotNumberMetadata.length > 0 ? lotNumberMetadata : undefined,
      lotSizeMetadata,
    });
  };

const __returned__ = { CORNER_HANDLE_RADIUS, MIDPOINT_HANDLE_RADIUS, props, emit, localElements, svgContainer, svgElement, svgOverlay, svgContent, overlayViewBox, zoomLevel, panX, panY, isDragging, dragStart, selectedElement, editingElement, isDrawing, drawingPoints, draggedCorner, draggedPolygonPointIndex, draggedCurveEdgeIndex, curveDragStart, curveHandleWasDragged, selectedLotId, lotSearch, assignments, lotNumberAdjustments, lotSizeAdjustments, isAssigned, svgStyle, assignedCount, unassignedCount, filteredLots, getLotName, calculateLotCenter, getLotNumberForElement, getLotSizeForElement, shouldShowLotSize, getLotNumberX, getLotNumberY, getLotNumberTransform, getLotSizeX, getLotSizeY, getLotSizeTransform, handleEditLotNumber, handleKeyPress, extractViewBox, handleElementClick, handleSelectAssignmentFromList, handleSelectLot, handleAssign, handleSkipElement, handleStartEdit, handleSaveEdit, handleCancelEdit, handleDeleteElement, handleCornerDragStart, handlePolygonCornerDragStart, handleCornerDrag, handleCornerDragEnd, handlePolygonCornerDrag, handlePolygonCornerDragEnd, handleCurveDragStart, getDistanceFromEdge, handleCurveDrag, handleCurveDragEnd, updateEditedShapeGeometry, handleUnassign, applyZoom, handleZoomIn, handleZoomOut, handleResetView, handleWheel, handleMouseDown, handleMouseMove, handleMouseUp, getSvgPoint, handleStartDrawing, handleDrawingPoint, handleUndoDrawingPoint, handleRemoveDrawingPoint, handleCancelDrawing, handleFinishDrawing, getDrawingPoints, handleConvertToPolygon, getPointDistance, normalizeEditablePolygonPoints, getPolygonMidpoints, handleMidpointClick, handleAddPolygonPoint, handleRemovePolygonPoint, getElementShapePath, getPolygonPoints, handleComplete, ref, computed, onMounted, onUnmounted, watch, get calculatePolygonCentroid() { return calculatePolygonCentroid }, get calculateBoundingBoxCenter() { return calculateBoundingBoxCenter }, get getLotLabelGap() { return getLotLabelGap }, get LOT_NUMBER_FONT_SIZE() { return LOT_NUMBER_FONT_SIZE }, get LOT_SIZE_FONT_SIZE() { return LOT_SIZE_FONT_SIZE }, get LOT_SIZE_DEFAULT_SCALE() { return LOT_SIZE_DEFAULT_SCALE }, get buildQuadraticShapePath() { return buildQuadraticShapePath }, get convertSvgPathToEditableGeometry() { return convertSvgPathToEditableGeometry }, get getEdgeMidpoint() { return getEdgeMidpoint }, get getQuadraticControlPoint() { return getQuadraticControlPoint }, get getQuadraticShapeBounds() { return getQuadraticShapeBounds } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})